import React, { memo, useEffect } from "react";

const TAMARA_SCRIPT_SRC =
  process.env.REACT_APP_TAMARA_WIDGET_URL ||
  "https://cdn.tamara.co/widget-v2/tamara-widget.js";

const PUBLIC_KEY = process.env.REACT_APP_TAMARA_PUBLIC_KEY;

// Tamara v2 reads window.tamaraWidgetConfig at script-init time. Set it
// once, BEFORE the <script> tag is appended, so the SDK can pick it up.
const setTamaraConfig = (lang) => {
  if (typeof window === "undefined") return;
  window.tamaraWidgetConfig = {
    lang: lang === "ar" ? "ar" : "en",
    country: "SA",
    publicKey: PUBLIC_KEY,
  };
};

let tamaraScriptPromise = null;

const loadTamaraScript = () => {
  if (typeof window === "undefined") return Promise.resolve();
  if (tamaraScriptPromise) return tamaraScriptPromise;

  tamaraScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TAMARA_SCRIPT_SRC}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") return resolve();
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = TAMARA_SCRIPT_SRC;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return tamaraScriptPromise;
};

// Per-element styling options passed as a JSON string in the `config`
// attribute (per Tamara v2 spec).
const ELEMENT_CONFIG = JSON.stringify({
  theme: "light",
  badgePosition: "",
  showExtraContent: "",
  hidePayInX: false,
});

const TamaraWidget = ({ amount, lang = "en", className = "" }) => {
  const numericAmount = Number(amount);
  const hasValidAmount = Number.isFinite(numericAmount) && numericAmount > 0;
  const canRender = hasValidAmount && Boolean(PUBLIC_KEY);

  useEffect(() => {
    if (!canRender) return;
    // Must run before loadTamaraScript so the SDK sees the config on init.
    setTamaraConfig(lang);

    let cancelled = false;
    loadTamaraScript()
      .then(() => {
        if (cancelled) return;
        if (window.TamaraWidgetV2 && typeof window.TamaraWidgetV2.refresh === "function") {
          window.TamaraWidgetV2.refresh();
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [canRender, numericAmount, lang]);

  if (!canRender) return null;

  return (
    <div className={className}>
      {/*
        key={numericAmount} forces a remount when the amount changes —
        Tamara's <tamara-widget> only observes ['uuid'], so attribute
        updates after first paint are otherwise ignored.

        No inline-type: lets the SDK's render switch pick the v2 bundle
        widget (first case in the ladder), which renders the new format
        per Tamara's Section 5.0 guidelines: "Pay X SAR/mo or in 4
        payments. Sharia-compliant. More options" with the brandmark on
        the right. This requires merchant config to have widgetOptions
        v2 enabled + a multi-instalment supportedInstalments array —
        both confirmed for this account (12-month plan + others).
      */}
      <tamara-widget
        key={numericAmount}
        type="tamara-summary"
        amount={String(numericAmount)}
        inline-type="2"
        config={ELEMENT_CONFIG}
      />
    </div>
  );
};

// Memoised so frequent parent re-renders (Home.js, MyCart, BundlDetail)
// don't re-render the widget when amount/lang/className haven't changed —
// every render otherwise triggers Tamara's attributeChangedCallback and
// re-validates the key.
export default memo(TamaraWidget);
